<template>
  <div class="mb-3">
    <label v-if="label" class="form-label">
      {{ label }}
      <small v-if="maxItems" class="text-muted">(Select up to {{ maxItems }})</small>
    </label>
    <p v-if="description" class="text-muted" style="font-size: var(--font-size-sm); margin-bottom: 1rem;">
      {{ description }}
    </p>

    <!-- Multi-Select Tagging Input -->
    <div class="multi-select-input-container">
      <!-- Selected Tags -->
      <div class="selected-tags-wrapper">
        <span
          v-for="itemId in modelValue"
          :key="itemId"
          class="tag"
          :class="getTagClass(itemId)"
        >
          <span class="tag-name">{{ getItemLabel(itemId) }}</span>
          <span v-if="getItemStatus(itemId)" class="tag-status">({{ getItemStatus(itemId) }})</span>
          <button
            v-if="isRemovable(itemId)"
            type="button"
            class="tag-remove"
            @click.prevent="removeItem(itemId)"
            :aria-label="`Remove ${getItemLabel(itemId)}`"
          >
            ×
          </button>
          <span v-else class="tag-locked" title="Locked">
            🔒
          </span>
        </span>

        <!-- Search Input -->
        <input
          type="text"
          v-model="searchText"
          @input="filterItems"
          @focus="showDropdown = true"
          @blur="setTimeout(() => { showDropdown = false }, 200)"
          :disabled="isAtMaxCapacity && modelValue.length > 0"
          :placeholder="placeholder"
          class="tag-search-input"
          autocomplete="off"
        >
      </div>

      <!-- Dropdown Suggestions -->
      <div v-if="showDropdown && filteredOptions.length > 0" class="multi-select-dropdown">
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="dropdown-item"
          :class="{ 'disabled': isDisabled(option.value) }"
          @click="selectItem(option.value)"
        >
          <span class="item-name">{{ option.label }}</span>
          <span v-if="getItemStatus(option.value)" class="item-badge">
            {{ getItemStatus(option.value) }}
          </span>
        </div>
      </div>

      <!-- Helper Text -->
      <small v-if="isAtMaxCapacity && modelValue.length > 0" class="text-muted d-block mt-2">
        {{ maxCapacityMessage }}
      </small>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

/**
 * MultiSelectTagInput.vue
 *
 * A reusable multi-select tagging field component with search and filtering.
 * Props:
 *   label: String (optional) - Field label
 *   description: String (optional) - Descriptive text
 *   options: Array<{ label, value, status?, disabled? }> - Available options
 *   modelValue: Array - Selected item values (v-model)
 *   maxItems: Number (optional) - Maximum items that can be selected
 *   placeholder: String (optional) - Search input placeholder
 *   maxCapacityMessage: String (optional) - Message when at capacity
 *   getStatusFn: Function (optional) - Custom status getter
 *   isDisabledFn: Function (optional) - Custom disabled check
 *   getTagClassFn: Function (optional) - Custom tag class generator
 *   isRemovableFn: Function (optional) - Custom removable check
 * Emits:
 *   update:modelValue - When selection changes
 *   remove-item - When a selected tag remove action is requested
 *
 * Usage:
 * <MultiSelectTagInput
 *   label="Working Groups"
 *   :options="groups"
 *   v-model="selectedGroupIds"
 *   :maxItems="3"
 *   placeholder="Search..."
 * />
 */
export default {
  name: 'MultiSelectTagInput',
  props: {
    label: { type: String, default: '' },
    description: { type: String, default: '' },
    options: { type: Array, required: true },
    modelValue: { type: Array, default: () => [] },
    maxItems: { type: Number, default: null },
    placeholder: { type: String, default: 'Search and add...' },
    maxCapacityMessage: {
      type: String,
      default: 'Maximum selections reached. Remove an item to add another.'
    },
    getStatusFn: { type: Function, default: null },
    isDisabledFn: { type: Function, default: null },
    getTagClassFn: { type: Function, default: null },
    isRemovableFn: { type: Function, default: null }
  },
  emits: ['update:modelValue', 'remove-item'],
  setup(props, { emit }) {
    const searchText = ref('')
    const showDropdown = ref(false)

    const isAtMaxCapacity = computed(() => {
      return props.maxItems && props.modelValue.length >= props.maxItems
    })

    const filteredOptions = computed(() => {
      const selected = new Set(props.modelValue.map((id) => String(id)))
      const search = searchText.value.toLowerCase().trim()

      return props.options.filter((option) => {
        // Skip already selected items
        if (selected.has(String(option.value))) {
          return false
        }
        // Filter by search term
        if (search && !option.label?.toLowerCase().includes(search)) {
          return false
        }
        return true
      })
    })

    const getItemLabel = (itemId) => {
      const option = props.options.find((o) => String(o.value) === String(itemId))
      return option?.label || itemId
    }

    const getItemStatus = (itemId) => {
      if (props.getStatusFn) {
        return props.getStatusFn(itemId)
      }
      const option = props.options.find((o) => String(o.value) === String(itemId))
      return option?.status || null
    }

    const isDisabled = (itemId) => {
      if (props.isDisabledFn) {
        return props.isDisabledFn(itemId)
      }
      const option = props.options.find((o) => String(o.value) === String(itemId))
      return option?.disabled || false
    }

    const isRemovable = (itemId) => {
      if (props.isRemovableFn) {
        return props.isRemovableFn(itemId)
      }
      return true
    }

    const getTagClass = (itemId) => {
      if (props.getTagClassFn) {
        return props.getTagClassFn(itemId)
      }
      return 'default'
    }

    const selectItem = (itemId) => {
      if (isDisabled(itemId)) {
        return
      }

      const newValue = [...props.modelValue, itemId]
      emit('update:modelValue', newValue)
      searchText.value = ''
      showDropdown.value = false
    }

    const removeItem = (itemId) => {
      emit('remove-item', itemId)
    }

    const filterItems = () => {
      // Keep dropdown open while typing
      showDropdown.value = true
    }

    return {
      searchText,
      showDropdown,
      isAtMaxCapacity,
      filteredOptions,
      getItemLabel,
      getItemStatus,
      isDisabled,
      isRemovable,
      getTagClass,
      selectItem,
      removeItem,
      filterItems
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: var(--font-weight-medium);
  margin-bottom: 0.5rem;
  color: #495057;
}

.text-muted {
  font-size: var(--font-size-sm);
  color: #6c757d !important;
}

/* Container */
.multi-select-input-container {
  position: relative;
}

/* Tags Wrapper */
.selected-tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #fff;
  min-height: 44px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.selected-tags-wrapper:focus-within {
  border-color: #17a2b8;
  box-shadow: 0 0 0 0.2rem rgba(23, 162, 184, 0.25);
}

/* Individual Tag */
.tag {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.7rem;
  border-radius: 20px;
  font-size: var(--font-size-sm);
  white-space: nowrap;
  background-color: #e3f2fd;
  color: #0056b3;
  border: 1px solid #90caf9;
}

.tag.pending-join {
  background-color: #f1f3f5;
  color: #6c757d;
  border-color: #d0d5dd;
}

.tag.pending-leave {
  background-color: #f1f3f5;
  color: #6c757d;
  border-color: #d0d5dd;
}

.tag.approved {
  background-color: #e8f5e9;
  color: #1b5e20;
  border-color: #81c784;
}

.tag.selected {
  background-color: #e0f2f1;
  color: #004d40;
  border-color: #80deea;
}

.tag.warning {
  background-color: #fff3e0;
  color: #e65100;
  border-color: #ffb74d;
}

.tag.success {
  background-color: #e8f5e9;
  color: #1b5e20;
  border-color: #81c784;
}

.tag.error {
  background-color: #ffebee;
  color: #b71c1c;
  border-color: #ef5350;
}

.tag-name {
  font-weight: var(--font-weight-medium);
}

.tag-status {
  font-size: var(--font-size-xs);
  opacity: 0.8;
}

.tag-remove {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: var(--font-size-lg);
  line-height: var(--line-height-tight);
  padding: 0;
  margin-left: 0.25rem;
  transition: opacity 0.15s;
}

.tag-remove:hover {
  opacity: 0.6;
}

.tag-locked {
  font-size: var(--font-size-sm);
  margin-left: 0.25rem;
}

/* Search Input */
.tag-search-input {
  flex: 1;
  min-width: 150px;
  border: none;
  outline: none;
  padding: 0.4rem 0;
  font-size: var(--font-size-sm);
  background: transparent;
}

.tag-search-input:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.tag-search-input::placeholder {
  color: #adb5bd;
}

/* Dropdown */
.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover:not(.disabled) {
  background-color: #f8f9fa;
}

.dropdown-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.item-name {
  font-weight: var(--font-weight-medium);
  color: #212529;
}

.item-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #e9ecef;
  color: #495057;
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.d-block {
  display: block;
}

.mt-2 {
  margin-top: 0.5rem;
}
</style>
